import { API_URL } from "config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "./store";
import {
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";

export const DEFAULT_PAGE_SIZE = 10;

export interface ListResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface GridModel {
  pagination?: GridPaginationModel;
  sorting?: GridSortModel;
  gridFilters?: GridFilterModel;
  filters?: Record<string, any>;
  /**
   * The grid fields whose endpoint declares a filter per lookup, so the
   * operator the user picked can be sent as a suffix on the field.
   *
   * Opt-in per grid, and deliberately not the default: django-filter DROPS a
   * parameter it does not know rather than rejecting it, so naming a field
   * here whose endpoint only offers `exact` turns a filter that was merely too
   * narrow into one that returns the whole table.
   */
  lookupFields?: string[];
}

/**
 * MUI's string operators, as the Django lookup each one filters on.
 *
 * `equals` is absent on purpose rather than missing: django-filter names the
 * exact filter after the bare field, so `asset__symbol` IS the equals filter
 * and a `__exact` suffix would name nothing. An operator absent here falls
 * through to that same bare field, which is what every grid sent before
 * operators were carried at all.
 *
 * Three of these are case-INSENSITIVE and two are not. `equals` resolves to
 * `exact` because renaming the legacy parameter would break every caller, and
 * Django has no case-insensitive `in`. So `aapl` finds AAPL under "contains"
 * and nothing under "equals" or "is any of" -- known, not accidental.
 */
const FILTER_LOOKUPS: Record<string, string> = {
  contains: "icontains",
  startsWith: "istartswith",
  endsWith: "iendswith",
  isAnyOf: "in",
};

/**
 * `isEmpty` and `isNotEmpty` carry no value of their own, so they are the two
 * operators that cannot be expressed as a lookup on `item.value`. They share
 * one boolean parameter instead: the endpoint reads true as "is empty" and
 * false as its complement.
 */
const EMPTINESS_OPERATORS: Record<string, boolean> = {
  isEmpty: true,
  isNotEmpty: false,
};

const filterItemToParam = (
  item: GridFilterItem,
  lookupFields?: string[],
): Record<string, any> => {
  if (!lookupFields?.includes(item.field)) {
    return { [item.field]: item.value };
  }

  // `hasOwn`, not a truthy lookup: a plain object inherits `toString` and
  // friends, so an operator named after one would read a FUNCTION off the
  // prototype and send it as the parameter value. Unreachable from the grid's
  // fixed operator set, and one word to make it stay that way.
  if (Object.hasOwn(EMPTINESS_OPERATORS, item.operator)) {
    return { [`${item.field}__isempty`]: EMPTINESS_OPERATORS[item.operator] };
  }

  const lookup = Object.hasOwn(FILTER_LOOKUPS, item.operator)
    ? FILTER_LOOKUPS[item.operator]
    : undefined;

  return {
    [lookup ? `${item.field}__${lookup}` : item.field]: item.value,
  };
};

export const gridModelsToQuery = (
  gridModel: GridModel,
): Record<string, any> | undefined => {
  let query = {};
  const {
    pagination,
    sorting,
    gridFilters: gridFilters,
    filters,
    lookupFields,
  } = gridModel;

  if (pagination) {
    query = {
      ...query,
      page: (pagination.page || 0) + 1,
      page_size: pagination.pageSize || DEFAULT_PAGE_SIZE,
    };
  }

  if (sorting && sorting.length) {
    query = {
      ...query,
      ordering: `${sorting[0].sort == "asc" ? "" : "-"}${sorting[0].field}`,
    };
  }

  if (gridFilters) {
    if (gridFilters.quickFilterValues) {
      query = { ...query, search: gridFilters.quickFilterValues };
    }
    if (gridFilters.items?.length) {
      query = {
        ...query,
        ...gridFilters.items.reduce(
          (acc, item) => ({ ...acc, ...filterItemToParam(item, lookupFields) }),
          {},
        ),
      };
    }
  }
  if (filters) {
    query = { ...query, ...filters };
  }

  return query;
};

export const api = createApi({
  reducerPath: "api",

  baseQuery: fetchBaseQuery({
    baseUrl: `${API_URL}`,
    prepareHeaders(headers, { getState }) {
      const state = getState() as RootState;
      const token = state.auth.accessToken;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),

  tagTypes: [
    "Auth",
    "FeatureFlag",
    "Integrations",
    "Secret",
    "SecretAudit",
    "Ticket",
  ],

  endpoints: () => ({}),
});

export default api;
